import { Component, OnInit, Signal, WritableSignal, computed, inject, signal, Renderer2, AfterViewInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Router, RouterLink } from '@angular/router';
import { CarouselComponent } from "../shared/carousel/carousel.component";
import { ProductsService } from '../product/products.service';
import { OrderService } from '../shared/order.service';
import { CommonModule } from '@angular/common';
import { DeliveriesService } from '../settings/account/deliveries/deliveries.service';
import { formatDateToReadableString } from '../shared/utils/formatting';
import { DeliveryInformation } from '../settings/account/deliveries/intarfaces';
import { SignalsStoreService } from '../shared/signals-store.service';
import { ModalContentService } from '../shared/modal-content/modal-content.service';
import { ModalContentTypes } from '../shared/constants/modal-content-types';
import { BundleEditionTypes, ProductOrder, RELATED_ORDER_PRODUCTS } from '../shared/types/order.type';
import { LocalStorageService } from '../shared/local-storage.service';
import { Session } from '../shared/types/session.type';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { isAbandonedUser, isAddressUpdateRequired, isNoAddressUser, trackBeginCheckoutEvent } from '../shared/common/utils';
import { ResolutionService } from "../shared/resolution.service";
import { environment } from '../../environments/environment';
import { TipsDonationsService } from '../shared/tips-donations.service';
import { CanDeactivateType } from '../shared/guards/deactivate/can-deactivate.types';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { BundleModifyTypes } from '../shared/types/flows-config.types';
import { SubscriptionsService } from '../settings/account/subscriptions/subscriptions.service';
import { finalize, tap } from 'rxjs';
import { MEDIUM } from '../../scss/responsive/responsive';
import { BundleItems } from '../product/product.types';
import { EmptyMessageComponent } from '../shared/empty-message/empty-message.component';
import JSConfetti from 'js-confetti';
import { afterNextRender } from '@angular/core';
import { LOCALSTORAGE_KEYS } from '../shared/constants/databases';
import { CalculationsService } from '../shared/calculations.service';
import { DataLayerService } from '../shared/data-layer/data-layer.service';
import { FirebaseCrudService } from '../shared/firebase-crud.service';
import { updateFirebaseOrderAndSubTrigger } from '../shared/common/order.utils';
import { OrderProductComponent } from './order-product/order-product.component';
import { AccountService } from '../shared/account.service';
import { NotificationService } from '../shared/notification/notification.service';

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss',
  providers: [SubscriptionsService],
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    MatSlideToggleModule,
    MatInputModule,
    CarouselComponent,
    MatProgressBarModule,
    EmptyMessageComponent,
    OrderProductComponent,
    NgbTooltip
  ]
})

export class OrderComponent implements OnInit, AfterViewInit {
  @ViewChild('layoutOrder', { static: false }) layoutOrder!: ElementRef;
  @ViewChild('orderActionWrap', { static: false }) orderActionWrap!: ElementRef;
  private mutationObserver!: MutationObserver;
  private renderer = inject(Renderer2);

  private modalContentService = inject(ModalContentService);
  private productsService = inject(ProductsService);
  deliveriesService = inject(DeliveriesService)
  localStorageService = inject(LocalStorageService);
  orderService = inject(OrderService);
  tipsDonationsService = inject(TipsDonationsService);
  signalsStoreService = inject(SignalsStoreService);
  private router = inject(Router);
  private subscriptionsService = inject(SubscriptionsService);
  private activeModal = inject(NgbModal);
  #calculationsService = inject(CalculationsService);
  #dataLayerService = inject(DataLayerService)
  #firebaseCrudService = inject(FirebaseCrudService);
  #accountService = inject(AccountService);
  private resolutionService = inject(ResolutionService);
  private notificationService = inject(NotificationService);

  isMobile = computed(() => this.resolutionService.isMobile());

  BundleEditionTypes = BundleEditionTypes;
  updateDonation = ModalContentTypes.UPDATE_DONATION;
  createDonation = ModalContentTypes.CREATE_DONATION;
  updateTip = ModalContentTypes.UPDATE_TIP;
  createTip = ModalContentTypes.CREATE_TIP;

  addressInfo: Signal<any | null> = computed(() => this.setUpAddressInfo())
  bundleCloned: WritableSignal<any> = signal(null);
  bundlesChanged: WritableSignal<Map<number, Map<number, Partial<BundleItems>>>> = signal(new Map());
  bundlesCloned: WritableSignal<Map<number, ProductOrder>> = signal(new Map());
  bundlesRemoved: WritableSignal<Map<number, Map<number, BundleItems>>> = signal(new Map());
  carouselBuyAgainProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.BUY_AGAIN, false));
  carouselFavoritesProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.FAVORITES, false));
  carouselSuggestedProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.SUGGESTED));
  collapseStatuses: { [key: string]: boolean } = {};
  coupon: WritableSignal<string> = signal(''); // actionTrigger

  showCouponErrorMessage: Signal<boolean> = computed(() => this.orderService.showCouponErrorMessage());

  couponErrorMessage: Signal<string> = computed(() => this.orderService.couponErrorMessage());

  creditsAmount: Signal<number> = computed(() => +this.getCredits());
  creditsBalance: Signal<number> = computed(() => this.getCreditsBalance());
  appliedCredits: Signal<number> = computed(() => this.getAppliedCredits());
  odooOrder: Signal<any | null> = computed(() => this.getOdooOrder());
  deliveryFee = computed(() => this.#calculationsService.getOrderDeliveryFee(this.odooOrder(), this.subTotal(), this.products()));
  hiddenCarouselProducts: Signal<number[]> = computed(() => this.signalsStoreService.hiddenCarouselProducts());

  minSpendForFreeDelivery: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.deliveryFee?.minSpend || 0);
  percentMinSpendForFreeDelivery: Signal<number> = computed(() => this.calculatePercentForFreeDelivery());

  deliveryInfo: Signal<any | null> = computed(() => this.setUpDeliveryInfo());
  donationAmount: WritableSignal<number> = signal(0);
  shouldShowTipsAndDonations: Signal<boolean> = computed(() => {
    const p = this.products();
    return p?.subscription?.length || p?.common?.length
  })
  tipAmount: Signal<number> = computed(() => this.getTipAmount());
  donationAmountVoluntary: Signal<number> = computed(() => this.getDonationVoluntaryAmount());

  hasPendingBundleChanges: WritableSignal<boolean> = signal(false)
  hasSelectedCustomQuantity: boolean = false;

  isAllowedOrder: Signal<boolean> = computed(() => this.hasMinOrder());
  hasPendingChanges: Signal<boolean> = computed(() => this.checkPendingChanges());
  showSavedOrderDisclaimer = signal(false);

  isCheckedSkipWeekDelivery: boolean = false;
  isConfirmedSkipWeekDelivery: boolean = false;
  isContentLoaded: Signal<any> = computed(() => this.signalsStoreService.isContentLoaded());
  isEditingBundle: boolean = false;
  // mapQuantityChanges: any = new Map()
  products: Signal<any | null> = computed(() => this.setUpProducts());
  skippedSubscriptions: Signal<any | null> = computed(() => this.#setUpSkippedSubscriptions());
  subTotal: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.subTotal || 0);
  grossSubTotal: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.subtotalBeforeDiscounts || this.odooOrder()?.paymentDetails?.subTotal || 0);
  totalSavings: Signal<number> = computed(() => this.#getTotalSavings());
  coupons: Signal<number> = computed(() => this.#getCoupons());
  taxes: Signal<number> = computed(() => this.odooOrder()?.paymentDetails?.taxes || 0);
  total: Signal<number> = computed(() => +this.#getTotal());
  isTemporaryRouteChange = signal(false);
  isDeletingProduct = signal(false);
  temporaryRouteChangeMessage = signal('');
  jsConfetti!: JSConfetti;
  //#region flows config
  hasMembershipFlow = signal(environment.config.flows.membership);
  showFreeDeliverySlider = signal(environment.config.flows.order.showFreeDeliverySlider);
  hasVoluntaryDonationFlow = signal(environment.config.flows.order.voluntaryDonationFlow);
  bundleModifyType = signal(environment.config.flows.bundles.modifyType);
  //#endregion

  allowPageExit = false;

  bundleModifyTypes = BundleModifyTypes;

  userChooseContinueShopping: boolean = false;

  subscriptionsLoaded = computed(() => this.products()?.subscription?.length > 0);

  commonLoaded = computed(() => this.products()?.common?.length > 0);

  /**
   * This property is isued to validate if the users can submit thier order despite not having reached the minimum price to save the order
   * because the bundle has not been published (its price is equal to zero).
   */
  orderHasUnpublishedBundle: Signal<boolean> = computed(() => {
    const products = this.products();
    const existsUnpublishedSubscriptionBundle = products?.subscription.some((p: any) => p?.bundle?.items && p.totalPrice === 0) || false;
    const existsUnpublishedAlaCarteBundle = products?.common.some((p: any) => p?.bundle?.items && p.totalPrice === 0) || false;
    return existsUnpublishedAlaCarteBundle || existsUnpublishedSubscriptionBundle;
  });

  marketStatus = computed(() => this.signalsStoreService.marketStatus());
  closedMarket = computed(() => {
    const im = this.isMobile();
    const ms = this.marketStatus();
    return {
      title: !ms.isOpen && im ?
        `Our Online Farmer's Market Open ${ms.openingDay.toUpperCase()} Morning!` :
        `Our Online Farmer's Market Open ${ms.openingDay.toUpperCase()} Morning!`,
      legend: `We're busy sourcing next week's availability! Check back Friday to begin shopping.`
    };
  })

  isUpdating = signal(false);

  contactEmail = signal('');
  preOrdersSkipDisclaimer = computed(() => {
    const ce = this.contactEmail();
    if (!ce) return '';
    return `You cannot skip this week’s order as it contains pre-ordered products. For assistance, please contact us at ${ce}.`
  })

  hasPreorderedProducts = computed(() => {
    const odooOrder = this.odooOrder();
    return (odooOrder?.products?.common || []).some((p: { isPreOrder: boolean; }) => p.isPreOrder);
  });

  async canDeactivate(): Promise<CanDeactivateType> {
    const canExit = await this.canExitOrder();
    return canExit || this.userChooseContinueShopping;
  }

  private async canExitOrder(): Promise<CanDeactivateType> {
    return new Promise(async (resolve, reject) => {
      const hasPendingProducts = this.hasPendingChanges()

      if (this.allowPageExit) {
        this.allowPageExit = false;
        return resolve(true);
      }

      if (hasPendingProducts) {
        const canExit = await this.openModalOrderNotSubmitted() as boolean;
        return resolve(canExit);
      }

      return resolve(true);
    })
  }

  ngOnInit(): void {
    this.orderService.getOrder(this.signalsStoreService.marketStatus);
    this.setUpDeliveryInfo();
  }

  ngAfterViewInit(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      this.setPaddingBottom();
    });

    this.#setMutationObserver();

    this.jsConfetti = new JSConfetti();

    setTimeout(() => {
      this.setPaddingBottom();
    }, 1);
  }

  constructor() {
    afterNextRender(() => {
      this.contactEmail.set(environment.config.contactEmail);
      this.#validateTemporaryRoute();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.setPaddingBottom();
  }

  isIphone(): boolean {
    return /iPhone/i.test(navigator.userAgent);
  }

  setPaddingBottom(): void {
    if ((window.innerWidth < MEDIUM) || this.isIphone()) {
      if (!this.orderActionWrap?.nativeElement) return;
      // setTimeout(() => {
      const orderActionWrapHeight = this.orderActionWrap.nativeElement.offsetHeight;
      // Set the padding-bottom to the height of .order-action-wrap
      this.renderer.setStyle(
        this.layoutOrder.nativeElement,
        'padding-bottom',
        `${orderActionWrapHeight}px`
      );
      // }, 2000);
    }
    else {
      if (!this.layoutOrder?.nativeElement) return;
      this.renderer.removeStyle(this.layoutOrder.nativeElement, 'padding-bottom');
    }
  }

  checkPendingChanges() {
    return (!!(this.products() &&
      [...this.products()?.common, ...this.products()?.subscription].some(product => !product.isConfirmed)) || this.hasPendingBundleChanges());
  }

  getTipAmount() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getTipAmount(this.odooOrder(), products);
  }

  getDonationVoluntaryAmount() {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getDonationVoluntaryAmount(this.odooOrder(), products);
  }

  private calculateProductOriginalTotalPrice(product: any): number {
    let totalPrice = 0;

    if (product.isBundle && !product?.bundle?.items?.length)
      return 0;

    if (product.bundle.isFixed) {
      totalPrice = +product.originalPrice;
      const premiumItems = product?.bundle?.items?.filter((pi: any) => pi.isPremiumAddon);
      if (premiumItems?.length) {
        totalPrice += premiumItems.reduce((total: number, bundleItem: any) => {
          const bundleItemPrice = (+bundleItem?.originalPrice * +bundleItem?.quantity) || 0
          return total + bundleItemPrice
        }, 0);
      }
    } else
      totalPrice = product.bundle.items.reduce((total: number, bundleItem: any) => {
        const bundleItemPrice = (bundleItem?.isRemoved ? 0 : (+bundleItem?.originalPrice * +bundleItem?.quantity) || 0)
        return total + bundleItemPrice
      }, 0);

    if (!totalPrice && !product.isBundle)
      totalPrice = product.originalPrice;

    return +((totalPrice * product.quantity).toFixed(2));
  }

  private processProducts(products: any[], subscription: boolean): any[] {
    return products.map(product => {
      const totalPrice = this.#calculationsService.calculateProductTotalPrice(product);
      const originalTotalPrice = this.calculateProductOriginalTotalPrice(product);
      this.collapseStatuses[`${product.id}${subscription ? '_1' : '_0'}`] = true;
      return { ...product, totalPrice, originalTotalPrice };
    });
  }

  getOdooOrder() {
    const odooOrder = this.orderService.order()

    if (!odooOrder || !odooOrder?.products?.common || !odooOrder?.products?.subscription)
      return odooOrder

    this.isCheckedSkipWeekDelivery = odooOrder?.isSkipped || false;

    return odooOrder
  }

  private setUpProducts(): any {
    const odooOrder = this.odooOrder();

    const { common: common = [], subscription: subscription = [] } = odooOrder?.products || {};

    const commonProducts = this.processProducts(common || [], false);
    const subscriptionProducts = this.processProducts(subscription || [], true);

    return {
      common: commonProducts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
      subscription: subscriptionProducts.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    }
  }

  private getCredits() {
    return +this.odooOrder()?.paymentDetails?.credits || 0
  }

  #getTotalSavings() {
    return this.#calculationsService.getOrderTotalSavings({
      subtotal: this.subTotal(),
      grossSubtotal: this.grossSubTotal(),
    })
  }

  #getTotal(): number {
    const products = this.#calculationsService.setUpOrderProducts(this.odooOrder());
    return this.#calculationsService.getOrderTotal(this.odooOrder(), products);
  }

  #getCoupons() {
    const coupons = this.odooOrder()?.paymentDetails?.coupons || [];
    return this.#calculationsService.getOrderCoupons(coupons);
  }

  private getCreditsBalance() {
    return this.odooOrder()?.paymentDetails?.creditsBalance || 0;
  }

  private getAppliedCredits() {
    return this.odooOrder()?.paymentDetails?.creditsApplied || 0;
  }

  private hasMinOrder(): boolean {
    return this.subTotal() >= (this.odooOrder()?.paymentDetails?.minOrder || 0);
  }

  onChangeSkipWeekDelivery() {
    this.isConfirmedSkipWeekDelivery = this.odooOrder()?.isSkipped ? false : true;
    if (this.isCheckedSkipWeekDelivery || !this.odooOrder()?.isSkipped) {
      if (environment.config.flows.order.askForDonationOnSkip)
        return this.openModalDonationSkippable();
      return this.openModalSkipWithDonationFlow();
    }

    const args = {
      orderId: this.odooOrder()?.id,
      isSkipping: false,
      donationAmount: 0,
    }

    this.orderService.skipOrder(args)
      .subscribe({
        next: (res) => {
          const isOrderSkipped = res?.data?.order?.isSkipped || false;
          this.isCheckedSkipWeekDelivery = isOrderSkipped;
          this.isConfirmedSkipWeekDelivery = isOrderSkipped;

          setTimeout(() => {
            this.#setMutationObserver();
          }, 500);
        }
      })
  }

  openModalTip(action: string) {
    this.signalsStoreService.odooTipAmount.set(this.tipAmount());
    this.modalContentService.openModal(ModalContentTypes.TIP)
      .closed
      .subscribe((res) => {
        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === this.createTip) {
          this.tipsDonationsService.create(payload, ModalContentTypes.TIP);
        } else {
          this.tipsDonationsService.update(payload, ModalContentTypes.TIP);
        }
      })
  }

  openModalDonation(action: string) {
    if (this.donationAmountVoluntary())
      this.signalsStoreService.odooDonationAmountVoluntary.set(this.donationAmountVoluntary());

    this.modalContentService.openModal(ModalContentTypes.DONATION)
      .closed
      .subscribe((res) => {
        if (!res?.amount) return;

        const payload = {
          amount: res?.amount,
          isRecurrent: res?.isRecurrent,
          applyToAllOrders: res?.applyToAllOrders
        }

        if (action === ModalContentTypes.CREATE_DONATION) {
          this.tipsDonationsService.create(payload, ModalContentTypes.DONATION);
        } else if (action === ModalContentTypes.UPDATE_DONATION) {
          this.tipsDonationsService.update(payload, ModalContentTypes.DONATION);
        }
      })
  }

  deleteDonationVoluntary() {
    if (this.odooOrder()?.paymentDetails?.donation?.isRecurrent) {
      this.modalContentService.openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {
          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.DONATION);
        })
    } else {
      this.tipsDonationsService.delete(0, ModalContentTypes.DONATION);
    }
  }

  deleteTip() {
    if (this.odooOrder()?.paymentDetails?.tip?.isRecurrent) {
      this.modalContentService.openModal(ModalContentTypes.DELETE_TIP_AND_DONATION)
        .closed
        .subscribe((res) => {
          const applyToAllOrders = Number(res?.applyToAllOrders);
          this.tipsDonationsService.delete(applyToAllOrders, ModalContentTypes.TIP);
        });
    } else {
      this.tipsDonationsService.delete(0, ModalContentTypes.TIP);
    }
  }

  openModalDonationSkippable() {
    this.signalsStoreService.totalOrderAmount.set(this.odooOrder()?.paymentDetails?.subTotal || 0)
    this.modalContentService.openModal(ModalContentTypes.DONATION_SKIPPABLE).closed
      .subscribe((res) => {
        if (!('isDonationCanceled' in res) || res?.isDonationCanceled)
          this.updateSkipWeekDeliveryFlags(false);
        else {
          const args = {
            orderId: this.odooOrder()?.id,
            isSkipping: true,
            donationAmount: res?.donationAmount || 0,
          }
          this.orderService.skipOrder(args)
            .subscribe({
              next: (skipRes) => {
                const isOrderSkipped = skipRes?.data?.order?.isSkipped || false;
                this.updateSkipWeekDeliveryFlags(isOrderSkipped);
              }
            });
        }
      })
  }

  openModalSkipWithDonationFlow() {
    this.modalContentService.openModal(ModalContentTypes.CONFIRMATION, {
      title: 'Are you sure?',
      textContent: `You are about to skip your delivery and you will not be receiving your order this week.  Please note that any customized or a la carte products will need to be re-added in future orders.`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Confirm',
    }).closed
      .subscribe((res: { confirm: boolean } | null) => {
        if (!res?.confirm) return this.updateSkipWeekDeliveryFlags(false);
        const args = {
          orderId: this.odooOrder()?.id,
          isSkipping: true,
          donationAmount: 0,
        }
        this.orderService.skipOrder(args)
          .subscribe({
            next: (skipRes) => {
              const isOrderSkipped = skipRes?.data?.order?.isSkipped || false;
              this.updateSkipWeekDeliveryFlags(isOrderSkipped);
            }
          });
      })
  }

  private updateSkipWeekDeliveryFlags(isOrderSkipped: boolean) {
    this.isCheckedSkipWeekDelivery = isOrderSkipped;
    this.isConfirmedSkipWeekDelivery = isOrderSkipped;
  }

  private async openModalOrderNotSubmitted() {
    return new Promise((resolve, reject) => {
      this.modalContentService.openModal(ModalContentTypes.CHECK_SUBMIT_ORDER, { closeable: true })
        .closed
        .subscribe((res) => {
          if (!res?.submitOrder) {
            this.orderService.openModalOrderNotSubmitted.set(false)

            if (res.canDeactivate) {
              this.userChooseContinueShopping = true;
              return resolve(true);
            }
            return resolve(false);
          }

          this.confirmOrder();

          return resolve(false);
        })
    })
  }

  private calculatePercentForFreeDelivery() {
    const subtotal = this.subTotal();
    const minSpend = this.minSpendForFreeDelivery();
    return (subtotal * 100) / minSpend;
  }

  private setUpAddressInfo() {
    const session: Session = this.signalsStoreService.sessionSignal() as any;

    if (!session) return null;

    const { street, city, zip: zipCode, stateCode: state } = session.address;
    const { location, name } = session?.pickUpInfo || {};

    const isAddressRequired = this.signalsStoreService.isAddressRequired();
    return {
      title: isAddressRequired ? 'Delivery Address' : 'Pick Up Address',
      text: isAddressRequired ? [street, city, state, zipCode].join(', ') : `${name} ${location ? `at ${location}` : ''}`
    };
  }

  private setUpDeliveryInfo() {
    if (!this.deliveriesService.deliveryZoneInfo()) return null

    const { deliveryDate, cutoffDate, cutoffTime, deliveryWindow } = (this.deliveriesService.deliveryZoneInfo()?.order || this.deliveriesService.deliveryZoneInfo()) as DeliveryInformation;
    const deliveryDateFormatted = formatDateToReadableString(deliveryDate)
    const cutoffDateFormatted = formatDateToReadableString(cutoffDate)

    const deliveryPickupDateText = this.signalsStoreService.isAddressRequired() ? 'Delivery Date: ' : 'Pick Up Date: '
    return {
      deliveryWindow,
      deliveryDateText: `${this.odooOrder()?.isSkipped ? 'Next ' : ''}${deliveryPickupDateText}${deliveryDateFormatted.dayName} ${deliveryDateFormatted.month} ${deliveryDateFormatted.day}`,
      cutoffDateText: `${cutoffDateFormatted.dayName} ${cutoffDateFormatted.month} ${cutoffDateFormatted.day} at ${cutoffTime}`
    }
  }

  private setUpCarouselItems(orderProductType: string, isOrderSuggestedProduct: boolean = true) {
    const hiddenProducts: number[] = this.hiddenCarouselProducts();
    if (this.odooOrder()?.isSkipped || this.isMobile()) return [];
    const selectedOrderProductType = Object.values(RELATED_ORDER_PRODUCTS).includes(orderProductType as RELATED_ORDER_PRODUCTS) ? orderProductType
      : RELATED_ORDER_PRODUCTS.SUGGESTED;

    if (!this.isContentLoaded || !this.odooOrder()?.relatedProducts?.[selectedOrderProductType] || !this.odooOrder()?.relatedProducts?.[selectedOrderProductType]?.length)
      if (!this.odooOrder()?.relatedProducts?.[selectedOrderProductType] || !this.odooOrder()?.relatedProducts?.[selectedOrderProductType]?.length)
        return []

    let items = this.odooOrder()?.relatedProducts?.[selectedOrderProductType];

    if (isOrderSuggestedProduct) {
      const allProductsId = [...this.products().common, ...this.products().subscription].map((product: any) => product.id)
      items = items.filter((product: any) => !allProductsId.includes(product.id));
    }

    if (hiddenProducts.length)
      items = items.filter((product: any) => hiddenProducts.indexOf(product.variantId) === -1)


    items = items.map((product: any) => {
      product.image = 'assets/images/logo-main.svg';
      product.name = product.name;
      if (typeof product?.price === 'number')
        product.price = product.price.toFixed(2);

      return ({
        content: {
          orderId: this.odooOrder()?.id,
          productDetails: { ...product, isSubscription: false },
          settings: { isCardInOrderPage: true, hideWhenWasOOS: true }
        },
      })
    })

    return items
  }

  async confirmOrder() {
    if (this.isUpdating())
      return;

    this.isUpdating.set(true);

    if (isAbandonedUser(this.localStorageService, this.modalContentService, this.activeModal))
      return this.isUpdating.set(false);
    if (isAddressUpdateRequired(this.localStorageService, this.modalContentService, this.activeModal, this.router))
      return this.isUpdating.set(false);
    if (isNoAddressUser(this.localStorageService, this.modalContentService, this.activeModal, this.router))
      return this.isUpdating.set(false);

    // Validate if the user can continue with the request if they are an explorer user and they add a payment method
    const res = await this.#accountService.validateExplorerUser('checkout');
    if (!res?.canContinueRequest) {
      this.notificationService.show({ text: 'Please add a payment method to continue with the order.', type: 'info' });
      this.isUpdating.set(false);
      return;
    }


    this.orderService.confirmOrder(this.odooOrder()?.id, this.jsConfetti).pipe(
      tap((res) => {
        updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService);
        trackBeginCheckoutEvent(this.#dataLayerService, [...res.subscriptionProducts, ...res.commonProducts], this.total(), this.coupon(), this.taxes(), this.deliveryFee(), this.tipAmount());
      }),
      finalize(() => this.isUpdating.set(false))
    ).subscribe(() => this.#showConfetti());
  }

  #showConfetti() {
    this.jsConfetti
      .addConfetti({
        emojis: ['🥕', '🍓', '🍋‍🟩', '🍋', '🍅', '🌽', '🥔', '🍆', '🍉', '🥦', '🧅', '🌶️'],
        emojiSize: this.isMobile() ? 70 : 35,
        confettiNumber: 80
      });
  }

  applyCoupon() {
    if (this.isUpdating()) return;

    this.isUpdating.set(true);
    const odooOrder = this.odooOrder();
    if (odooOrder?.id)
      this.orderService.applyCouponWithOrder(this.coupon, +odooOrder.id)
        .pipe(
          finalize(() => this.isUpdating.set(false))
        ).subscribe();
  }

  unskipProduct(product: any) {
    this.subscriptionsService.update(product.subscription.id, { unskip: true }, true).subscribe()
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  #validateTemporaryRoute() {

    const currentSessionInfo: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
    if (!currentSessionInfo)
      return;

    const { temporaryChange } = currentSessionInfo?.deliveryInfo ?? {};
    this.isTemporaryRouteChange.set(!!temporaryChange?.active);

    if (this.isTemporaryRouteChange())
      this.temporaryRouteChangeMessage.set(temporaryChange?.message ?? '');
  }

  #setMutationObserver() {
    if (this.orderActionWrap && this.orderActionWrap.nativeElement) {
      this.mutationObserver.observe(this.orderActionWrap.nativeElement, {
        childList: true,
        subtree: true
      });
    }
  }

  #setUpSkippedSubscriptions() {
    const skippedSubscriptions = this.odooOrder()?.skippedSubscriptions || [];
    return skippedSubscriptions;
  }

}
